import { Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';

import { bicValidator } from '../../_validators/bic.validator';
import { businessEmailValidator } from '../../_validators/email.validator';
import { ibanValidator } from '../../_validators/iban.validator';
import { isoDateValidator } from '../../_validators/iso-date.validator';
import { norwegianBankAccountValidator } from '../../_validators/norwegian-bank-account.validator';
import { norwegianNationalIdentityNumberValidator } from '../../_validators/norwegian-national-identity-number.validator';

export const declarationControlName = 'bekreftelse';

const maxAmount = 999999999999;

const maxAmountValidator: ValidatorFn = (control: AbstractControl) =>
  control.value !== null && control.value !== '' && Number(control.value) > maxAmount ? { maxAmount: true } : null;

const otherIncomeGroups = [
  { name: 'lonnOgAnnetArbeidsvederlagHosAnnenArbeidsgiver', hasDescription: false },
  { name: 'inntektFraSelvstendigNaering', hasDescription: false },
  { name: 'dagpengerUnderArbeidsloshet', hasDescription: false },
  { name: 'sykepengerFraNav', hasDescription: false },
  { name: 'andreYtelserFraNav', hasDescription: true },
  { name: 'eventuelleAndreInntekterEllerYtelser', hasDescription: true }
];

interface VisibilityRule {
  controlNames: string[];
  isVisible: (values: any) => boolean;
}

@Injectable()
export class NorgeApplicationFormService {
  public readonly form: FormGroup;

  private lastAddressVariant: string | null = null;

  constructor() {
    this.form = new FormGroup({
      arbeidsgiver: this.buildEmployerPanel(),
      personopplysninger: this.buildPersonalDetailsPanel(),
      opplysningerOmArbeidsforhold: this.buildEmploymentDetailsPanel(),
      feriepengerOgFerieavvikling: this.buildHolidayPayPanel(),
      eierandeler: this.buildOwnershipPanel(),
      rollerOgRelasjoner: this.buildRolesPanel(),
      overdragelseAvKrav: this.buildClaimTransferPanel(),
      sykmeldtPermittertEllerArbeidsledig: this.buildAbsencePanel(),
      lonnsOgFeriepengekrav: this.buildWageClaimsPanel(),
      utgifterIForbindelseMedKonkursbegjaering: this.buildPetitionExpensesPanel(),
      fradragForAndreInntekter: this.buildOtherIncomePanel(),
      tilleggsopplysninger: new FormGroup({ eventuelleTilleggssopplysningerEllerMerknader: this.optionalText() }),
      erklaring: new FormGroup({ [declarationControlName]: new FormControl(false, Validators.requiredTrue) })
    });
  }

  public createRow(arrayName: string): FormGroup {
    switch (arrayName) {
      case 'pliktigTrekkUtenomSkattetrekk':
        return this.buildMandatoryDeductionRow();
      case 'ferieperiodeR':
      case 'foreldrepermisjonsperiodeR':
        return this.buildHolidayPeriodRow();
      case 'tidligereKonkursbo':
        return this.buildPreviousEstateRow();
      case 'sykmeldingsperiode':
        return this.buildSickLeaveRow();
      case 'permitteringsperiodeR':
        return this.buildLayOffRow();
      case 'fastBruttolonn':
        return this.buildGrossSalaryRow();
      case 'annetArbeidsvederlag':
        return this.buildOtherRemunerationRow();
      case 'feriepenger':
        return this.buildHolidayPayClaimRow();
      case 'andreKrav':
        return this.buildOtherClaimRow();
      default:
        throw new Error(`Unknown datagrid ${arrayName}`);
    }
  }

  public addressVariant(personalDetails: any): string | null {
    if (personalDetails.borDuINorge === 'nei') {
      return 'utenlandsk';
    }

    if (personalDetails.borDuINorge === 'ja'
      && (personalDetails.vegadresseEllerPostboksadresse === 'vegadresse' || personalDetails.vegadresseEllerPostboksadresse === 'postboksadresse')) {
      return personalDetails.vegadresseEllerPostboksadresse;
    }

    return null;
  }

  private buildEmployerPanel(): FormGroup {
    return new FormGroup({
      hvaErBoetsNavn: this.requiredText()
    });
  }

  private buildPersonalDetailsPanel(): FormGroup {
    const panel = new FormGroup({
      fornavnSoker: this.requiredText(),
      etternavnSoker: this.requiredText(),
      harDuNorskFodselsnummerEllerDNummer: this.requiredText(),
      fodselsnummerDNummerSoker: this.requiredText([norwegianNationalIdentityNumberValidator]),
      fodselsdatoDdMmAaaaSoker: this.requiredText([isoDateValidator]),
      borDuINorge: this.requiredText(),
      vegadresseEllerPostboksadresse: this.requiredText(),
      coSoker: this.optionalText(),
      vegadresseSoker: this.requiredText(),
      postboksNrSoker: this.requiredText(),
      bygningSoker: this.optionalText(),
      postnrSoker: this.optionalText(),
      poststedSoker: this.optionalText(),
      regionSoker: this.optionalText(),
      landSoker: this.requiredText(),
      telefonnummer: this.requiredText(),
      ePostadresse: this.requiredText([businessEmailValidator]),
      harIkkeEPost: new FormControl(false),
      bostedskommune: this.requiredText(),
      harDuNorskEllerUtenlandskKonto: this.requiredText(),
      kontoNummer: this.requiredText([norwegianBankAccountValidator]),
      iban: this.requiredText([ibanValidator]),
      swift: this.requiredText([bicValidator]),
      jegHarFrikort: new FormControl(false),
      oppgiProsenttrekketDitt: this.requiredText(),
      onskerDuITilleggAOpplyseOmHvilkenSkattetabellDuHarHatt: this.requiredText(),
      oppgiDinSkattetabell: this.requiredText(),
      harDuAndrePliktigeTrekkITilleggTilSkattetrekk: this.requiredText(),
      pliktigTrekkUtenomSkattetrekk: new FormArray([this.buildMandatoryDeductionRow()])
    });

    this.bindVisibility(panel, [
      { controlNames: ['fodselsnummerDNummerSoker'], isVisible: values => values.harDuNorskFodselsnummerEllerDNummer === 'ja' },
      { controlNames: ['fodselsdatoDdMmAaaaSoker'], isVisible: values => values.harDuNorskFodselsnummerEllerDNummer === 'nei' },
      { controlNames: ['vegadresseEllerPostboksadresse'], isVisible: values => values.borDuINorge === 'ja' },
      { controlNames: ['coSoker', 'postnrSoker', 'poststedSoker'], isVisible: values => this.addressVariant(values) != null },
      { controlNames: ['vegadresseSoker'], isVisible: values => this.addressVariant(values) === 'vegadresse' },
      { controlNames: ['postboksNrSoker'], isVisible: values => this.addressVariant(values) === 'postboksadresse' || this.addressVariant(values) === 'utenlandsk' },
      { controlNames: ['bygningSoker', 'regionSoker', 'landSoker'], isVisible: values => this.addressVariant(values) === 'utenlandsk' },
      { controlNames: ['ePostadresse'], isVisible: values => !values.harIkkeEPost },
      { controlNames: ['kontoNummer'], isVisible: values => values.harDuNorskEllerUtenlandskKonto === 'norsk' },
      { controlNames: ['iban', 'swift'], isVisible: values => values.harDuNorskEllerUtenlandskKonto === 'utenlandsk' },
      { controlNames: ['oppgiDinSkattetabell'], isVisible: values => values.onskerDuITilleggAOpplyseOmHvilkenSkattetabellDuHarHatt === 'ja' },
      { controlNames: ['pliktigTrekkUtenomSkattetrekk'], isVisible: values => values.harDuAndrePliktigeTrekkITilleggTilSkattetrekk === 'ja' }
    ], () => this.applyAddressValidators(panel));

    return panel;
  }

  private buildEmploymentDetailsPanel(): FormGroup {
    const panel = new FormGroup({
      hvaVarStillingenDinIVirksomheten: this.requiredText(),
      fraHvilkenDatoBleDuAnsattDdMmAaaa: this.requiredText([isoDateValidator]),
      hvilkenDatoMottokDuOppsigelsenDdMmAaaa: this.requiredText([isoDateValidator]),
      oppgiLengdenPaOppsigelsestidenDin: this.requiredText(),
      harDuTariffavtaleOmOppsigelsestid: this.requiredText(),
      avtaltLonn: this.requiredAmount(),
      avtaltLonnPer: new FormControl<string[]>([], Validators.required),
      avtaltAntallTimerPerUke: this.requiredAmount(),
      redegjor: this.requiredText(),
      avtaltLonningsdato: this.requiredText(),
      harDuMottattLonnFraArbeidsgiverenDinIPeriodenDuHarVaertAnsatt: this.requiredText(),
      mottattLonnFraOgMedDatoDdMmAaaa: this.requiredText([isoDateValidator]),
      mottattLonnTilOgMedDatoDdMmAaaa: this.requiredText([isoDateValidator]),
      harDineLonnsEllerArbeidsvilkarBlittEndretITidenForKonkursapning: this.requiredText(),
      oppgiDatoForSisteEndringDdMmAaaa: this.requiredText([isoDateValidator]),
      hvaSlagsEndringHarDuHattILonnsEllerArbeidsvilkareneDine: this.requiredText()
    });

    this.bindVisibility(panel, [
      { controlNames: ['avtaltAntallTimerPerUke'], isVisible: values => values.avtaltLonnPer.includes('time') },
      { controlNames: ['redegjor'], isVisible: values => values.avtaltLonnPer.includes('akkordlonn') || values.avtaltLonnPer.includes('provisjonEllerBonus') },
      { controlNames: ['mottattLonnFraOgMedDatoDdMmAaaa', 'mottattLonnTilOgMedDatoDdMmAaaa'], isVisible: values => values.harDuMottattLonnFraArbeidsgiverenDinIPeriodenDuHarVaertAnsatt === 'ja' },
      { controlNames: ['oppgiDatoForSisteEndringDdMmAaaa', 'hvaSlagsEndringHarDuHattILonnsEllerArbeidsvilkareneDine'], isVisible: values => values.harDineLonnsEllerArbeidsvilkarBlittEndretITidenForKonkursapning === 'ja' }
    ]);

    return panel;
  }

  private buildHolidayPayPanel(): FormGroup {
    const panel = new FormGroup({
      harDuAvvikletFerieEllerKommerDuTilAAvvikleFerieIDenPeriodenDuSokerDekketLonnFor: this.requiredText(),
      ferieperiodeR: new FormArray([this.buildHolidayPeriodRow()]),
      harDuHoyereProsentsatsEnnDetSomFolgerAvFerieloven10Nr2: this.requiredText(),
      oppgiProsentsats: this.requiredAmount([Validators.max(100)]),
      hvorMangeFeriedagerHarDuAvvikletIAr: this.requiredAmount(),
      harDuOverfortFeriedagerFraTidligereAr: this.requiredText(),
      hvorMangeFeriedagerErOverfortFraTidligereAr: this.requiredAmount()
    });

    this.bindVisibility(panel, [
      { controlNames: ['ferieperiodeR'], isVisible: values => values.harDuAvvikletFerieEllerKommerDuTilAAvvikleFerieIDenPeriodenDuSokerDekketLonnFor === 'ja' },
      { controlNames: ['oppgiProsentsats'], isVisible: values => values.harDuHoyereProsentsatsEnnDetSomFolgerAvFerieloven10Nr2 === 'ja' },
      { controlNames: ['hvorMangeFeriedagerErOverfortFraTidligereAr'], isVisible: values => values.harDuOverfortFeriedagerFraTidligereAr === 'ja' }
    ]);

    return panel;
  }

  private buildOwnershipPanel(): FormGroup {
    const panel = new FormGroup({
      harDuHattEierandelerIVirksomhetenIHeleEllerDelerAvSoknadsperioden: this.requiredText(),
      hvaVarStorrelsenPaEierandelenDin: this.requiredAmount([Validators.max(100)]),
      fraHvilkenDatoEideDuDenneAndelenDdMmAaaa: this.requiredText([isoDateValidator]),
      tilHvilkenDatoEideDuDenneAndelenDdMmAaaa: this.optionalDate()
    });

    this.bindVisibility(panel, [
      {
        controlNames: ['hvaVarStorrelsenPaEierandelenDin', 'fraHvilkenDatoEideDuDenneAndelenDdMmAaaa', 'tilHvilkenDatoEideDuDenneAndelenDdMmAaaa'],
        isVisible: values => values.harDuHattEierandelerIVirksomhetenIHeleEllerDelerAvSoknadsperioden === 'ja'
      }
    ]);

    return panel;
  }

  private buildRolesPanel(): FormGroup {
    const panel = new FormGroup({
      harDuHattEnLedendeStillingIVirksomhetenISoknadsperioden: this.requiredText(),
      hvilkenStillingHaddeDu: this.requiredText(),
      harDuVaertStyremedlemISoknadsperioden: this.requiredText(),
      varDuArbeidstakernesValgteRepresentant: this.requiredText(),
      harNoenDuHarEnNaerRelasjonTilHattEierandelerEnLedendeStillingEllerVaertStyremedlemIVirksomheten: this.requiredText(),
      hvaSlagsRelasjonHarHaddeDere: this.requiredText(),
      redegjorNaermereForEierandelensStorrelseStillingenEllerVervet: this.requiredText(),
      harDuTidligereFattLonnsgarantidekningIAndreKonkursbo: this.requiredText(),
      tidligereKonkursbo: new FormArray([this.buildPreviousEstateRow()])
    });

    this.bindVisibility(panel, [
      { controlNames: ['hvilkenStillingHaddeDu'], isVisible: values => values.harDuHattEnLedendeStillingIVirksomhetenISoknadsperioden === 'ja' },
      { controlNames: ['varDuArbeidstakernesValgteRepresentant'], isVisible: values => values.harDuVaertStyremedlemISoknadsperioden === 'ja' },
      {
        controlNames: ['hvaSlagsRelasjonHarHaddeDere', 'redegjorNaermereForEierandelensStorrelseStillingenEllerVervet'],
        isVisible: values => values.harNoenDuHarEnNaerRelasjonTilHattEierandelerEnLedendeStillingEllerVaertStyremedlemIVirksomheten === 'ja'
      },
      { controlNames: ['tidligereKonkursbo'], isVisible: values => values.harDuTidligereFattLonnsgarantidekningIAndreKonkursbo === 'ja' }
    ]);

    return panel;
  }

  private buildClaimTransferPanel(): FormGroup {
    const panel = new FormGroup({
      erKravetDittHeltEllerDelvisOverdrattAvEllerPaAnnenMateGattOverTilAndre: this.requiredText(),
      narBleKravetDittOverdrattDdMmAaaa: this.requiredText([isoDateValidator]),
      hvemBleKravetDittOverdrattTil: this.requiredText()
    });

    this.bindVisibility(panel, [
      {
        controlNames: ['narBleKravetDittOverdrattDdMmAaaa', 'hvemBleKravetDittOverdrattTil'],
        isVisible: values => values.erKravetDittHeltEllerDelvisOverdrattAvEllerPaAnnenMateGattOverTilAndre === 'ja'
      }
    ]);

    return panel;
  }

  private buildAbsencePanel(): FormGroup {
    const panel = new FormGroup({
      harDuVaertSykmeldtIHeleEllerDelerAvSoknadsperioden: this.requiredText(),
      sykmeldingsperiode: new FormArray([this.buildSickLeaveRow()]),
      harDuVaertPermittertIHeleEllerDelerAvSoknadsperioden: this.requiredText(),
      permitteringsperiodeR: new FormArray([this.buildLayOffRow()]),
      harDuHattLonnetForeldrepermisjonIHeleEllerDelerAvSoknadsperioden: this.requiredText(),
      foreldrepermisjonsperiodeR: new FormArray([this.buildHolidayPeriodRow()]),
      harDuMeldtDegForNavSomArbeidssoker: this.requiredText(),
      fraHvilkenDatoErDuRegistrertSomArbeidssokerDdMmAaaa: this.requiredText([isoDateValidator]),
      forklarArsakenTilAtDuIkkeHarMeldDegForNavSomArbeidssoker: this.requiredText(),
      harDuAlleredeFattEllerRegnerDuMedAFaArbeidIgjen: this.requiredText(),
      fraHvilkenDatoHarDuEllerRegnerDuMedAStarteINyttArbeidIgjenDdMmAaaa: this.requiredText([isoDateValidator]),
      navnPaNyArbeidsgiver: this.requiredText()
    });

    this.bindVisibility(panel, [
      { controlNames: ['sykmeldingsperiode'], isVisible: values => values.harDuVaertSykmeldtIHeleEllerDelerAvSoknadsperioden === 'ja' },
      { controlNames: ['permitteringsperiodeR'], isVisible: values => values.harDuVaertPermittertIHeleEllerDelerAvSoknadsperioden === 'ja' },
      { controlNames: ['foreldrepermisjonsperiodeR'], isVisible: values => values.harDuHattLonnetForeldrepermisjonIHeleEllerDelerAvSoknadsperioden === 'ja' },
      { controlNames: ['fraHvilkenDatoErDuRegistrertSomArbeidssokerDdMmAaaa'], isVisible: values => values.harDuMeldtDegForNavSomArbeidssoker === 'ja' },
      { controlNames: ['forklarArsakenTilAtDuIkkeHarMeldDegForNavSomArbeidssoker'], isVisible: values => values.harDuMeldtDegForNavSomArbeidssoker === 'nei' },
      {
        controlNames: ['fraHvilkenDatoHarDuEllerRegnerDuMedAStarteINyttArbeidIgjenDdMmAaaa', 'navnPaNyArbeidsgiver'],
        isVisible: values => values.harDuAlleredeFattEllerRegnerDuMedAFaArbeidIgjen === 'ja'
      }
    ]);

    return panel;
  }

  private buildWageClaimsPanel(): FormGroup {
    const panel = new FormGroup({
      hvaSlagsKravOnskerDuASendeInn: new FormControl<string[]>([], Validators.required),
      fastBruttolonn: new FormArray([this.buildGrossSalaryRow()]),
      annetArbeidsvederlag: new FormArray([this.buildOtherRemunerationRow()]),
      feriepenger: new FormArray([this.buildHolidayPayClaimRow()]),
      andreKrav: new FormArray([this.buildOtherClaimRow()])
    });

    this.bindVisibility(panel, [
      { controlNames: ['fastBruttolonn'], isVisible: values => values.hvaSlagsKravOnskerDuASendeInn.includes('lonn') },
      { controlNames: ['annetArbeidsvederlag'], isVisible: values => values.hvaSlagsKravOnskerDuASendeInn.includes('annetArbeidsvederlag') },
      { controlNames: ['feriepenger'], isVisible: values => values.hvaSlagsKravOnskerDuASendeInn.includes('feriepenger') },
      { controlNames: ['andreKrav'], isVisible: values => values.hvaSlagsKravOnskerDuASendeInn.includes('andreKrav') }
    ]);

    return panel;
  }

  private buildPetitionExpensesPanel(): FormGroup {
    const panel = new FormGroup({
      harDuHattUtgifterIForbindelseMedKonkursbegjaeringen: this.requiredText(),
      oppgiBelop: this.requiredAmount()
    });

    this.bindVisibility(panel, [
      { controlNames: ['oppgiBelop'], isVisible: values => values.harDuHattUtgifterIForbindelseMedKonkursbegjaeringen === 'ja' }
    ]);

    return panel;
  }

  private buildOtherIncomePanel(): FormGroup {
    const panel = new FormGroup({
      harDuHattAndreInntekterISoknadsperioden: this.requiredText(),
      hvaSlagsInntektHarDuHattISoknadsperioden: new FormControl<string[]>([], Validators.required),
      ...Object.fromEntries(otherIncomeGroups.map(group => [group.name, this.buildOtherIncomeGroup(group.hasDescription)]))
    });

    this.bindVisibility(panel, [
      { controlNames: ['hvaSlagsInntektHarDuHattISoknadsperioden'], isVisible: values => values.harDuHattAndreInntekterISoknadsperioden === 'ja' },
      ...otherIncomeGroups.map(group => ({
        controlNames: [group.name],
        isVisible: (values: any) => values.harDuHattAndreInntekterISoknadsperioden === 'ja' && values.hvaSlagsInntektHarDuHattISoknadsperioden.includes(group.name)
      }))
    ]);

    return panel;
  }

  private buildOtherIncomeGroup(hasDescription: boolean): FormGroup {
    const controls: Record<string, AbstractControl> = {
      bruttobelop: this.requiredAmount(),
      fraOgMedDatoDdMmAaaa: this.requiredText([isoDateValidator]),
      tilOgMedDatoDdMmAaaa: this.requiredText([isoDateValidator]),
      kanBelopetTrekkesFraLonnskravetDitt: this.requiredText()
    };

    if (hasDescription) {
      controls['hvaSlagsYtelse'] = this.requiredText();
    }

    return new FormGroup(controls);
  }

  private buildPreviousEstateRow(): FormGroup {
    return new FormGroup({
      navnPaVirksomheten: this.requiredText()
    });
  }

  private buildSickLeaveRow(): FormGroup {
    return new FormGroup({
      sykmeldtFraDatoDdMmAaaa: this.requiredText([isoDateValidator]),
      sykmeldtTilDatoDdMmAaaa: this.optionalDate(),
      sykmeldingsgrad: this.requiredAmount([Validators.max(100)])
    });
  }

  private buildLayOffRow(): FormGroup {
    return new FormGroup({
      permittertFraDatoDdMmAaaa: this.requiredText([isoDateValidator]),
      permittertTilDatoDdMmAaaa: this.optionalDate(),
      permitteringsgrad: this.requiredAmount([Validators.max(100)])
    });
  }

  private buildGrossSalaryRow(): FormGroup {
    return new FormGroup({
      bruttolonn: this.requiredAmount(),
      fraOgMedDatoDdMmAaaa: this.requiredText([isoDateValidator]),
      tilOgMedDatoDdMmAaaa: this.requiredText([isoDateValidator])
    });
  }

  private buildOtherRemunerationRow(): FormGroup {
    return new FormGroup({
      belop: this.requiredAmount(),
      fraOgMedDatoDdMmAaaa: this.requiredText([isoDateValidator]),
      tilOgMedDatoDdMmAaaa: this.requiredText([isoDateValidator]),
      forklaringSpesifiseringAvArbeidsvederlaget: this.requiredText()
    });
  }

  private buildHolidayPayClaimRow(): FormGroup {
    return new FormGroup({
      belop: this.requiredAmount(),
      opptjentFraOgMedDatoDdMmAaaa: this.requiredText([isoDateValidator]),
      opptjentTilOgMedDatoDdMmAaaa: this.requiredText([isoDateValidator])
    });
  }

  private buildOtherClaimRow(): FormGroup {
    return new FormGroup({
      bruttobelop: this.requiredAmount(),
      fraOgMedDatoDdMmAaaa: this.requiredText([isoDateValidator]),
      tilOgMedDatoDdMmAaaa: this.requiredText([isoDateValidator]),
      forklaringSpesifiseringAvKravet: this.requiredText()
    });
  }

  private buildMandatoryDeductionRow(): FormGroup {
    return new FormGroup({
      typeTrekk: this.requiredText(),
      belop: this.requiredAmount()
    });
  }

  private buildHolidayPeriodRow(): FormGroup {
    return new FormGroup({
      fraDatoDdMmAaaa: this.requiredText([isoDateValidator]),
      tilDatoDdMmAaaa: this.requiredText([isoDateValidator])
    });
  }

  private bindVisibility(panel: FormGroup, rules: VisibilityRule[], afterApply?: () => void): void {
    const apply = () => {
      const values = panel.getRawValue();

      for (const rule of rules) {
        const isVisible = rule.isVisible(values);

        for (const controlName of rule.controlNames) {
          const control = panel.get(controlName) as AbstractControl;

          if (isVisible && control.disabled) {
            control.enable({ emitEvent: false });
          } else if (!isVisible && control.enabled) {
            control.disable({ emitEvent: false });
          }
        }
      }

      afterApply?.();
    };

    panel.valueChanges.subscribe(apply);
    apply();
  }

  private applyAddressValidators(panel: FormGroup): void {
    const variant = this.addressVariant(panel.getRawValue());
    if (variant === this.lastAddressVariant) {
      return;
    }

    this.lastAddressVariant = variant;

    const isNorwegianAddress = variant === 'vegadresse' || variant === 'postboksadresse';
    const postalCodeControl = panel.get('postnrSoker') as AbstractControl;
    const postalTownControl = panel.get('poststedSoker') as AbstractControl;

    postalCodeControl.setValidators(isNorwegianAddress ? [Validators.required, Validators.minLength(4), Validators.maxLength(4)] : []);
    postalTownControl.setValidators(isNorwegianAddress ? [Validators.required] : []);
    postalCodeControl.updateValueAndValidity({ emitEvent: false });
    postalTownControl.updateValueAndValidity({ emitEvent: false });
  }

  private requiredText(extraValidators: ValidatorFn[] = []): FormControl {
    return new FormControl('', [Validators.required, ...extraValidators]);
  }

  private optionalText(): FormControl {
    return new FormControl('');
  }

  private optionalDate(): FormControl {
    return new FormControl('', [isoDateValidator]);
  }

  private requiredAmount(extraValidators: ValidatorFn[] = []): FormControl {
    return new FormControl<number | null>(null, [Validators.required, Validators.min(0), maxAmountValidator, ...extraValidators]);
  }
}
